import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useAccount,
  usePublicClient,
  useSwitchChain,
  useWalletClient,
} from "wagmi";
import { getPrefixKey, keys, QueryKeys } from "../../core/queries/queries-keys";

import {
  ChainType,
  OnChainExecutionData,
  RouteResponse,
  Token,
} from "@0xsquid/squid-types";
import { CHAIN_IDS, nativeEvmTokenAddress } from "../../core/constants";
import { isEvmosChain, isProblematicConnector, sleep } from "../../services";
import { useSquidChains } from "../chains/useSquidChains";
import { useSquidStore } from "../store/useSquidStore";
import { useSwap } from "../swap/useSwap";
import { useMultiChainWallet } from "../wallet/useMultiChainWallet";
import { useSigner } from "../wallet/useSigner";
import { useErc20Allowance } from "./useErc20Allowance";

export const useApproval = ({
  squidRoute,
}: {
  squidRoute: RouteResponse["route"] | undefined;
}) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const queryClient = useQueryClient();
  const squid = useSquidStore((state) => state.squid);
  const { fromChain, fromToken, fromPrice, isSameChain } = useSwap();
  const { evmSigner } = useSigner({ chain: fromChain });
  const { connector: activeConnector } = useAccount();
  const { getChainType } = useSquidChains();

  const { switchChainAsync } = useSwitchChain();

  const {
    connectedAddress: { address: sourceUserAddress },
  } = useMultiChainWallet(fromChain);

  const isSagaEvmSameChainSwap =
    isSameChain &&
    fromChain?.chainId.toString() === CHAIN_IDS.SAGA_EVM.toString();

  // Swaps on SagaEVM are regular EVM transactions, following the standard ERC20 approve/allowance mechanism.
  // However, when bridging from SagaEVM to other Saga chainlets, approve/allowance is not needed
  // as these transfers interact with the SagaEVM ICS20 precompile contract.
  // See https://github.com/sagaxyz/cosmos-evm/tree/main/precompiles/ics20
  const erc20AllowanceQueryEnabled =
    isSagaEvmSameChainSwap ||
    (fromChain?.chainType === ChainType.EVM && !isEvmosChain(fromChain));

  const {
    hasAllowance,
    query: allowanceQuery,
    allowanceInWei,
  } = useErc20Allowance({
    tokenAddress: fromToken?.address,
    ownerAddress: sourceUserAddress,
    spenderAddress: (squidRoute?.transactionRequest as OnChainExecutionData)
      ?.target,
    amount: BigInt(squidRoute?.params.fromAmount ?? "0"),
    chainId: Number(fromChain?.chainId),
    transactionType: squidRoute?.transactionRequest?.type,
    enabled: erc20AllowanceQueryEnabled,
  });

  /**
   * Checking if spending tokens is allowed for this source address
   * On Success: storing the transaction
   * On Error: Showing the error message if any
   * @returns {boolean} approved
   */
  const routeApproved = useQuery(
    keys().routeApproved(
      squidRoute,
      allowanceInWei,
      erc20AllowanceQueryEnabled,
      hasAllowance
    ),
    async () => {
      if (erc20AllowanceQueryEnabled) {
        return hasAllowance;
      }

      return true;
    },
    {
      enabled:
        !!squidRoute &&
        !!sourceUserAddress &&
        // Only wait for allowance if that query is actually enabled
        (!erc20AllowanceQueryEnabled ||
          (!allowanceQuery?.isLoading && allowanceQuery?.isFetched)),
    }
  );

  // USDT has a very specific way of handling approvals
  // ```
  /// To change the approve amount you first have to reduce the addresses`
  //  allowance to zero by calling `approve(_spender, 0)` if it is not
  //  already 0 to mitigate the race condition described here:
  //  https://github.com/ethereum/EIPs/issues/20#issuecomment-263524729
  // ```
  // This is why we had an unpredictable gas error for USDT approvals
  // So it needs a custom gas limit
  //
  // TODO: We're only doing it for USDT on Ethereum Mainnet, need to check if it's needed for USDT on other chains
  const approveSpecificTokenToZero = async (token: Token) => {
    if (
      walletClient &&
      token.symbol.toLowerCase() === "usdt" &&
      token.chainId === CHAIN_IDS.ETHEREUM &&
      fromToken
    ) {
      const fromChainRouterAddress = (
        squidRoute?.transactionRequest as OnChainExecutionData
      )?.target;

      const allowance = await publicClient?.readContract({
        address: token.address as `0x${string}`,
        abi: [
          {
            constant: true,
            inputs: [
              { name: "_owner", type: "address" },
              { name: "_spender", type: "address" },
            ],
            name: "allowance",
            outputs: [{ name: "remaining", type: "uint256" }],
            type: "function",
          },
        ],
        functionName: "allowance",
        args: [
          sourceUserAddress as `0x${string}`,
          fromChainRouterAddress as `0x${string}`,
        ],
      });

      if (
        allowance &&
        BigInt(allowance.toString()) > BigInt(0) &&
        publicClient
      ) {
        const approveAbi = [
          {
            constant: false,
            inputs: [
              { name: "_spender", type: "address" },
              { name: "_value", type: "uint256" },
            ],
            name: "approve",
            outputs: [],
            type: "function",
          },
        ] as const;

        const contractCallArgs = {
          address: token.address as `0x${string}`,
          abi: approveAbi,
          functionName: "approve",
          args: [fromChainRouterAddress as `0x${string}`, BigInt(0)],
          account: sourceUserAddress as `0x${string}`,
        } as const;

        let request;

        try {
          const estimatedGas =
            await publicClient.estimateContractGas(contractCallArgs);

          const gasLimit = (estimatedGas * 120n) / 100n;

          const simulation = await publicClient.simulateContract({
            ...contractCallArgs,
            gas: gasLimit,
          });
          request = simulation.request;
        } catch (estimationError) {
          const simulation = await publicClient.simulateContract({
            ...contractCallArgs,
            gas: 100000n, // Fallback gas limit
          });
          request = simulation.request;
        }

        if (request) {
          await walletClient.writeContract(request);
        }
      }
    }
    return true;
  };

  /**
   * Manually approve route if necessary
   */
  const approveRoute = useMutation(
    async () => {
      try {
        if (
          fromToken?.address.toLowerCase() ===
          nativeEvmTokenAddress.toLowerCase()
        ) {
          return true;
        }
        if (!!squidRoute && walletClient && fromToken && evmSigner && squid) {
          await approveSpecificTokenToZero(fromToken);

          try {
            // If needed, we can switch the chain here
            // If already on the correct chain, this will do nothing
            await switchChainAsync({ chainId: Number(fromChain?.chainId) });
          } catch (error) {
            console.error("Error switching network:", error);
          }

          const approveTx = await squid.approveRoute({
            route: squidRoute,
            signer: evmSigner,
            // For security reasons, we don't want to allow infinite approvals in our frontends
            executionSettings: {
              infiniteApproval: false,
            },
          });

          if (isProblematicConnector(activeConnector)) {
            await sleep(3_000);
          }

          await approveTx?.wait();

          return true;
        }
        return false;
      } catch (error) {
        // Keep the error in the console to debug future issues
        console.error(error);
        return false;
      }
    },
    {
      onSuccess: async () => {
        await allowanceQuery?.refetch();
        queryClient.invalidateQueries(getPrefixKey(QueryKeys.RouteApproved));

        // After an approval, we refetch the transaction query with all required parameters
        // This is to ensure we're using the latest expiry timestamp
        if (squidRoute) {
          queryClient.refetchQueries({
            queryKey: keys().transaction(
              squidRoute.params.fromChain,
              squidRoute.params.toChain,
              squidRoute.params.toToken,
              squidRoute.params.fromToken,
              fromPrice,
              squidRoute.params.slippage,
              squidRoute.params.fromAddress,
              squidRoute.params.bypassGuardrails,
              squidRoute.params.toAddress,
              squidRoute.params.fallbackAddresses?.[0]?.address,
              squidRoute.params.quoteOnly,
              getChainType(squidRoute.params.fromChain),
              squidRoute.params.preHook,
              squidRoute.params.postHook,
              // TODO: update types
              (squidRoute.params as any)?.overrideGasRefundAddress
            ),
          });
        }
      },
    }
  );

  return {
    routeApproved,
    approveRoute,
    allowanceInWei,
  };
};
